import React, { useState } from 'react';
import { FlaskConical, ShieldCheck, Play, Download, Undo2, AlertTriangle, CheckCircle2, FileText } from 'lucide-react';
import { RubyGemMark } from '../../../components/RubyGemMark';
import { toastService } from '../../../services/toastService';
import { migrationApi, type MigrationJob, type MigrationEvent } from '../../../services/migrationApi';
import { Btn, Card, Banner, Modal, StatTile, EDITABLE, RUBY, dryRunRunning, fmtDateTime, toastApiError } from './shared';
import { openDownload } from './FilesStep';

const lastEvent = (job: MigrationJob, kind: string): MigrationEvent | undefined =>
  [...(job.events || [])].reverse().find((e) => e.kind === kind);

const reportKeys = (job: MigrationJob) => {
  const base = `reports/${job.tenantId}/${job.jobId}`;
  return { dryRun: `${base}/dry-run.json`, apply: `${base}/apply-report.json`, failed: `${base}/failed-rows.csv` };
};

type StepProps = { job: MigrationJob; onJob: (j: MigrationJob) => void; reload: () => Promise<void> | void };

// ── Dry run ─────────────────────────────────────────────────────────────────
export const DryRunStep: React.FC<StepProps> = ({ job, onJob, reload }) => {
  const [busy, setBusy] = useState(false);
  const running = dryRunRunning(job);
  const ev = lastEvent(job, 'dry_run');
  const blocked = !!ev && ev.message.startsWith('Dry run blocked');
  const allowed = EDITABLE.includes(job.status) && !running;

  const start = async () => {
    setBusy(true);
    try {
      const r = await migrationApi.dryRun(job.tenantId, job.jobId);
      if (r?.job) onJob(r.job); else await reload();
    } catch (e) {
      toastApiError(e, 'Could not start the dry run');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title={<span className="flex items-center gap-2"><FlaskConical size={16} className="text-stone-500" />Dry run</span>}
      actions={<Btn kind="black" icon={<Play size={16} />} busy={busy} disabled={!allowed} onClick={start}>{job.dryRunAt ? 'Run again' : 'Run dry run'}</Btn>}
    >
      <p className="text-sm text-stone-500 mb-4">Checks every included item and counts what would be created — nothing is written to the catalogue.</p>
      {running ? (
        <div className="flex items-center gap-3 text-sm text-stone-700 py-2">
          <span style={{ color: RUBY }}><RubyGemMark size={24} drawing color="currentColor" /></span>
          Dry run in progress — started {fmtDateTime(job.dryRunRequestedAt)}
        </div>
      ) : ev ? (
        <div className="space-y-3">
          <Banner tone={blocked ? 'amber' : 'green'} icon={blocked ? <AlertTriangle size={16} /> : <CheckCircle2 size={16} />}>
            {ev.message}
            <div className="text-xs opacity-70 mt-1">{fmtDateTime(ev.at)}</div>
          </Banner>
          {job.status === 'mapped' && !blocked && <p className="text-xs text-amber-700">Items were edited after this dry run — run it again before approving.</p>}
          <Btn icon={<FileText size={14} />} onClick={() => openDownload(job, job.reportKey || reportKeys(job).dryRun)}>dry-run.json</Btn>
        </div>
      ) : (
        <p className="text-sm text-stone-400">No dry run yet.{!EDITABLE.includes(job.status) ? ' Analyse the files first.' : ''}</p>
      )}
    </Card>
  );
};

// ── Approve & apply ─────────────────────────────────────────────────────────
export const ApplyStep: React.FC<StepProps> = ({ job, onJob, reload }) => {
  const [busy, setBusy] = useState<'approve' | 'apply' | null>(null);
  const [confirmApply, setConfirmApply] = useState(false);
  const [rubyPhotos, setRubyPhotos] = useState(false);
  const photoless = Number((job.stats as any)?.rubyPhotoCandidates || 0);
  const p = job.progress || {};
  const total = p.total || 0;
  const running = ['backing_up', 'applying', 'stocking'].includes(job.status);
  const s = job.stats || {};

  const approve = async () => {
    setBusy('approve');
    try {
      const r = await migrationApi.approve(job.tenantId, job.jobId, rubyPhotos && photoless > 0);
      onJob(r.job);
      toastService.success('Approved — ready to apply');
    } catch (e) {
      toastApiError(e, 'Could not approve');
    } finally {
      setBusy(null);
    }
  };
  const apply = async () => {
    setBusy('apply');
    try {
      const r = await migrationApi.apply(job.tenantId, job.jobId);
      setConfirmApply(false);
      if (r?.job) onJob(r.job); else await reload();
      toastService.success('Import started');
    } catch (e) {
      toastApiError(e, 'Could not start the import');
    } finally {
      setBusy(null);
    }
  };

  const bar = (label: string, done: number, of: number, failed = 0) => {
    const pct = of ? Math.min(100, Math.round(((done + failed) / of) * 100)) : 0;
    return (
      <div>
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="text-stone-700 font-medium">{label}</span>
          <span className="text-stone-500">{done}{failed ? ` ok · ${failed} failed` : ''} / {of || '—'}</span>
        </div>
        <div className="h-3 rounded-full bg-stone-100 overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${RUBY}, #9F1239)` }} />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <Card title={<span className="flex items-center gap-2"><ShieldCheck size={16} className="text-stone-500" />Approve &amp; apply</span>}>
        <ol className="space-y-4">
          <li className="flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <div className="font-medium text-stone-900">1 · Approve the dry run</div>
              <div className="text-sm text-stone-500">
                {job.approvedAt ? `Approved by ${job.approvedBy || 'an admin'} · ${fmtDateTime(job.approvedAt)}` : 'Only available once a clean dry run is ready.'}
              </div>
            </div>
            <Btn kind="black" icon={<ShieldCheck size={16} />} busy={busy === 'approve'} disabled={job.status !== 'dry_run_ready'} onClick={approve}>Approve</Btn>
            {photoless > 0 && job.status === 'dry_run_ready' && (
              <button
                type="button"
                role="switch"
                aria-checked={rubyPhotos}
                onClick={() => setRubyPhotos((v) => !v)}
                className="w-full min-h-[44px] flex items-center gap-3 rounded-xl border border-stone-200 bg-white px-4 py-3 text-left hover:border-stone-400"
              >
                <span style={{ color: RUBY }}><RubyGemMark size={20} color="currentColor" /></span>
                <span className="flex-1">
                  <span className="block text-sm font-medium text-stone-900">Find missing photos with Ruby</span>
                  <span className="block text-xs text-stone-500">
                    Ruby looks up {photoless} product{photoless === 1 ? '' : 's'} without a photo by barcode and name. Charged to the client's Ruby Tokens; anything Ruby can't find stays a draft.
                  </span>
                </span>
                <span className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${rubyPhotos ? 'bg-stone-900' : 'bg-stone-300'}`}>
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${rubyPhotos ? 'left-[22px]' : 'left-0.5'}`} />
                </span>
              </button>
            )}
            {job.rubyPhotos && job.status !== 'dry_run_ready' && (
              <div className="w-full text-xs text-stone-500 flex items-center gap-2"><span style={{ color: RUBY }}><RubyGemMark size={14} color="currentColor" /></span>Ruby is finding photos for products without one.</div>
            )}
          </li>
          <li className="flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <div className="font-medium text-stone-900">2 · Apply to the live catalogue</div>
              <div className="text-sm text-stone-500">Backs up the current catalogue first, then imports in batches and sets opening stock.</div>
            </div>
            <Btn kind="ruby" icon={<Play size={16} />} busy={busy === 'apply'} disabled={job.status !== 'approved'} onClick={() => setConfirmApply(true)}>Apply</Btn>
          </li>
        </ol>
      </Card>

      {(running || total > 0) && (
        <Card title={<span className="flex items-center gap-2">{running && <span style={{ color: RUBY }}><RubyGemMark size={18} drawing color="currentColor" /></span>}Progress</span>}>
          <div className="space-y-4">
            {job.status === 'backing_up' && <p className="text-sm text-stone-600">Backing up the current catalogue…</p>}
            {bar('Products', p.done || 0, total, p.failed || 0)}
            {(job.status === 'stocking' || (p.stockDone || 0) > 0) && bar('Opening stock', p.stockDone || 0, Math.max(0, total - (p.failed || 0)))}
          </div>
        </Card>
      )}

      {confirmApply && (
        <Modal
          title="Apply this import?"
          icon={Play}
          onClose={() => setConfirmApply(false)}
          busy={busy === 'apply'}
          footer={<><Btn onClick={() => setConfirmApply(false)} disabled={busy === 'apply'}>Cancel</Btn><Btn kind="black" busy={busy === 'apply'} onClick={apply}>Apply now</Btn></>}
        >
          <p className="text-sm">
            <b>{s.toCreate ?? 0}</b> products ({s.variants ?? 0} variants) will be created in <b>{job.tenantName || job.tenantId}</b>'s live catalogue.
            A backup is taken first and the whole import can be rolled back from the Report step.
          </p>
        </Modal>
      )}
    </div>
  );
};

// ── Report + rollback ───────────────────────────────────────────────────────
export const ReportStep: React.FC<StepProps> = ({ job, onJob, reload }) => {
  const [rollbackOpen, setRollbackOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const keys = reportKeys(job);
  const applied = lastEvent(job, 'applied');
  const rolled = lastEvent(job, 'rolled_back');
  const p = job.progress || {};
  const canRollback = ['applied', 'failed', 'stocking'].includes(job.status);
  const hasApplyReport = ['applied', 'rolling_back', 'rolled_back'].includes(job.status) || !!applied;
  const WORD = 'ROLL BACK';

  const rollback = async () => {
    setBusy(true);
    try {
      const r = await migrationApi.rollback(job.tenantId, job.jobId);
      setRollbackOpen(false);
      setTyped('');
      if (r?.job) onJob(r.job); else await reload();
      toastService.success('Rollback started');
    } catch (e) {
      toastApiError(e, 'Could not start the rollback');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatTile label="Planned" value={p.total} />
        <StatTile label="Written" value={p.done} tone="ruby" />
        <StatTile label="Failed" value={p.failed} tone={p.failed ? 'amber' : 'plain'} />
        <StatTile label="Stock set" value={p.stockDone} />
      </div>
      {job.status === 'failed' && job.error && <Banner tone="rose" icon={<AlertTriangle size={16} />}>{job.error}</Banner>}
      {applied && <Banner tone="green" icon={<CheckCircle2 size={16} />}>{applied.message}<div className="text-xs opacity-70 mt-1">{fmtDateTime(applied.at)}</div></Banner>}
      {job.status === 'rolling_back' && (
        <Banner tone="sky" icon={<span style={{ color: RUBY }}><RubyGemMark size={16} drawing color="currentColor" /></span>}>Rolling back — archiving the imported products…</Banner>
      )}
      {rolled && <Banner tone="amber">{rolled.message}<div className="text-xs opacity-70 mt-1">{fmtDateTime(rolled.at)}</div></Banner>}

      <Card title="Downloads">
        <div className="flex flex-wrap gap-2">
          <Btn icon={<Download size={14} />} disabled={!hasApplyReport} onClick={() => openDownload(job, keys.apply)}>apply-report.json</Btn>
          <Btn icon={<Download size={14} />} disabled={!hasApplyReport} onClick={() => openDownload(job, keys.failed)}>failed-rows.csv</Btn>
          <Btn icon={<Download size={14} />} disabled={!job.backupKey} onClick={() => job.backupKey && openDownload(job, job.backupKey)}>Catalogue backup</Btn>
          <Btn icon={<Download size={14} />} disabled={!job.dryRunAt} onClick={() => openDownload(job, keys.dryRun)}>dry-run.json</Btn>
        </div>
      </Card>

      <Card title="Roll back">
        <div className="flex items-start gap-3 flex-wrap">
          <p className="flex-1 min-w-[220px] text-sm text-stone-500">
            Archives every product this import created. Categories and brands it created are left in place.
          </p>
          <Btn kind="danger" icon={<Undo2 size={16} />} disabled={!canRollback} onClick={() => setRollbackOpen(true)}>Roll back import</Btn>
        </div>
      </Card>

      {rollbackOpen && (
        <Modal
          title="Roll back this import?"
          icon={Undo2}
          onClose={() => { if (!busy) { setRollbackOpen(false); setTyped(''); } }}
          busy={busy}
          footer={(
            <>
              <Btn onClick={() => { setRollbackOpen(false); setTyped(''); }} disabled={busy}>Cancel</Btn>
              <Btn kind="danger" busy={busy} disabled={typed.trim().toUpperCase() !== WORD} onClick={rollback} icon={<Undo2 size={16} />}>Roll back</Btn>
            </>
          )}
        >
          <p className="text-sm mb-3">
            Every product imported into <b>{job.tenantName || job.tenantId}</b> by this migration will be archived and disappear from their POS and website.
          </p>
          <label htmlFor="mig-rollback" className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1 block">Type {WORD} to confirm</label>
          <input
            id="mig-rollback"
            autoFocus
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && typed.trim().toUpperCase() === WORD) rollback(); }}
            className="w-full h-11 rounded-lg border border-stone-300 px-3 text-sm outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-500/15"
          />
        </Modal>
      )}
    </div>
  );
};
